import './table-grid.scss';

import {
  Box,
  FormControl,
  MenuItem,
  Pagination,
  PaginationItem,
  Select,
  SelectChangeEvent,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRowsProp,
  GridSortModel,
  useGridApiContext,
} from '@mui/x-data-grid';
import React, { ReactNode } from 'react';

interface TableGridProps {
  rows: GridRowsProp;
  columns: GridColDef[];
  pageSize?: number;
  currentPage?: number;
  className?: string;
  totalResults?: number;
  pageSizeOptions?: number[];
  rowHeight?: number;
  isLoading?: boolean;
  sortModel?: GridSortModel;
  isServerSorting?: boolean;
  isDetailsPageTable?: boolean;
  isPaymentTable?: boolean;
  noDataMessage?: string;
  message?: string;
  handleChangePageNumber?: (pageNumber: number) => void;
  handlePageSizeChange?: (pageSize: number) => void;
  handleSortModelChange?: (sortModel: GridSortModel) => void;
}

const CustomPagination = ({
  handleChangePageNumber,
  handleUpdatePageSize,
  totalResults,
  currentPage = 0,
  pageSize,
  showPagination,
}: {
  handleChangePageNumber?: (pageSize: number) => void;
  handleUpdatePageSize?: (
    event: SelectChangeEvent<number>,
    child: ReactNode
  ) => void;

  totalResults?: number;
  currentPage?: number;
  showPagination?: boolean;
  pageSize: number;
}) => {
  const apiRef = useGridApiContext();

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    apiRef.current.setPage(value);
    handleChangePageNumber && handleChangePageNumber(value - 1);
  };
  const from = currentPage * pageSize + 1;
  const to = Math.min(Number(totalResults), (currentPage + 1) * pageSize);

  return (
    <Box className="pagination">
      <Typography className="pagination-label">ROWS PER PAGE</Typography>
      <FormControl variant="outlined" size="small">
        <Select
          value={pageSize}
          onChange={handleUpdatePageSize}
          className="page-options"
        >
          {[10, 25, 50, 100].map((size) => (
            <MenuItem className="page-option" key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {showPagination && (
        <Box className="pagination-wrapper">
          <div className="pagination-text">
            <span className="font-bold">{`${from}-${to}`}</span> {'OF '}
            <span className="font-bold">{`${totalResults}`}</span>
          </div>
          <Pagination
            count={Math.ceil(Number(totalResults) / pageSize) ?? 1}
            page={currentPage + 1}
            onChange={handlePageChange}
            className="pagination"
            renderItem={(item) =>
              item.type === 'previous' || item.type === 'next' ? (
                <PaginationItem {...item} />
              ) : null
            }
          />
        </Box>
      )}
    </Box>
  );
};

export const TableGrid: React.FC<TableGridProps> = ({
  rows,
  columns,
  pageSize = 10,
  message,
  totalResults,
  pageSizeOptions = [5, 10, 25, 100],
  currentPage,
  className,
  isLoading = false,
  sortModel,
  isServerSorting = false,
  rowHeight,
  isDetailsPageTable,
  isPaymentTable,
  noDataMessage,
  handleChangePageNumber,
  handleSortModelChange,
  handlePageSizeChange,
}) => {
  const isXXL = useMediaQuery('(min-width:2100px)');
  const isXL = useMediaQuery('(min-width:1950px)');
  const isLarge = useMediaQuery('(min-width:1850px)');
  const isMedium = useMediaQuery('(min-width:1650px)');
  const isSmall = useMediaQuery('(min-width:1400px)');

  const handleUpdatePageSize = (
    event: SelectChangeEvent<number>,
    child: ReactNode
  ) => {
    handleChangePageNumber && handleChangePageNumber(0);
    handlePageSizeChange &&
      handlePageSizeChange(Number(event.target.value) as number);
  };

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <DataGrid
        key={JSON.stringify(rows)} // Use a key to force re-render when rows update
        loading={isLoading}
        rows={isLoading ? [] : rows}
        columns={columns}
        pageSizeOptions={pageSizeOptions}
        initialState={{
          pagination: {
            paginationModel: { pageSize, page: 0 },
          },
        }}
        className={className}
        disableVirtualization={true}
        rowHeight={rowHeight}
        getRowSpacing={(params) => ({
          top: 6,
          bottom: 6,
        })}
        sortingMode={isServerSorting ? 'server' : 'client'}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        slots={{
          pagination: (props) => (
            <CustomPagination
              pageSize={pageSize}
              handleChangePageNumber={handleChangePageNumber}
              handleUpdatePageSize={handleUpdatePageSize}
              totalResults={totalResults}
              currentPage={currentPage}
              showPagination={
                !isLoading && rows.length > 0 && !isDetailsPageTable
              }
              {...props}
            />
          ),
          noRowsOverlay: () => (
            <CustomNoRowsOverlay
              noDataMessage={noDataMessage}
              message={message}
            />
          ), // Custom "No Rows" message
        }}
        sx={{
          width: '100%',
          height: isPaymentTable
            ? '100%'
            : isDetailsPageTable
            ? 'auto'
            : // Adjust height based on page size and screen size
            pageSize >= 100
            ? isXXL
              ? '90vh'
              : isXL || isLarge
              ? '800px'
              : isMedium
              ? '700px'
              : isSmall
              ? '700px'
              : '500px'
            : pageSize >= 50
            ? isXXL
              ? '90vh'
              : isXL || isLarge
              ? '760px'
              : isMedium
              ? '650px'
              : isSmall
              ? '550px'
              : '450px'
            : isXXL
            ? '90vh'
            : isXL
            ? '760px'
            : isLarge
            ? '740px'
            : isMedium
            ? '700px'
            : isSmall
            ? '500px'
            : '400px',
          position: isPaymentTable ? 'relative' : undefined,
          border: 'none',
          overscrollBehavior: 'none',
          '& .MuiDataGrid-main': {
            border: 'none',
          },
          '& .MuiDataGrid-menuIcon': {
            display: 'none !important ', // Hide the menu icon
          },
          '& .MuiDataGrid-root': {
            borderColor: 'transparent',
            border: 'none', // Remove the outer border around the DataGrid
          },
          '& .MuiDataGrid-row': {
            border: '1px solid #cccccc',
            borderLeft: '1px solid #cccccc',
            backgroundColor: '#fff',
            borderRadius: '3px',
            fontSize: '14px',
            fontWeight: 600,
            width: '99.7%',
            color: 'black',
            boxSizing: 'border-box', // Ensure consistent sizing across platforms
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            borderTop: 'none',
            fontWeight: '500',
            padding: '24px',
            backgroundColor: isDetailsPageTable ? '#e7fdfb' : 'transparent',
          },
          '& .MuiDataGrid-container--top [role="row"], & .MuiDataGrid-container--bottom [role="row"]':
            {
              backgroundColor: '#cefcf6',
              fontSize: '14px',
              fontWeight: 800,
            },
          '& .MuiDataGrid-columnHeaders': {
            marginBottom: '12px',
            border: 'none',
            backgroundColor: '#cefcf6',
          },
          '& .MuiDataGrid-virtualScrollerRenderZone': {
            width: '100%',
            paddingBottom: isDetailsPageTable
              ? '0px'
              : // Adjust padding based on page size and screen size
              // Add extra padding for Windows to ensure last row is fully visible
              pageSize >= 100
              ? isXXL || isXL || isLarge
                ? '220px'
                : '170px'
              : pageSize >= 50
              ? isXXL || isXL || isLarge
                ? '170px'
                : '140px'
              : pageSize >= 25
              ? isXXL || isXL || isLarge
                ? '140px'
                : '120px'
              : isXXL || isXL || isLarge
              ? '120px'
              : '120px',
          },
          '& .MuiDataGrid-columnHeader': {
            border: 'none !important',
            height: '32px !important',
            padding: '0 24px',

            // Remove the bottom border of the header
          },
          '& .MuiDataGrid-columnHeader:focus': {
            outline: 'none', // Remove the default outline on focus
          },
          '& .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none', // Remove the default outline when focus is within the header cell
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: '600', // Make header text bold
            fontSize: '12px',
            lineHeight: '15.88px',
            letterSpacing: '0.5px',
          },
          '& .MuiDataGrid-virtualScroller': {
            marginTop: '0px !important', // Ensure no margin/padding at the top of the virtual scroller
            minHeight: '150px',
          },
          '& .MuiDataGrid-overlayWrapper': {
            minHeight: '150px',
          },
          '& .MuiDataGrid-columnSeparator--sideRight': {
            display: 'none', // Hide the right-side border separator between header cells
          },
          '& .MuiDataGrid-footerContainer': {
            display: isDetailsPageTable || isLoading ? 'none' : 'flex',
            justifyContent: 'center',
            bottom: '72px',
            left: 0,
            padding: '2px !important',
            zIndex: 10,
            borderTop: '1px solid #cccccc',
            position: 'fixed',
            width: '100%',
            maxWidth: 'calc(100vw - 20px)', // Account for scrollbar width on Windows
            background: 'white',
            boxSizing: 'border-box', // Ensure padding is included in width calculation
          },
          '& .MuiDataGrid-filler': {
            display: isDetailsPageTable ? 'none' : 'block',
          },
          '& .MuiDataGrid-selectedRowCount': {
            display: 'none', // Hide the "1 row selected" text
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none', // Remove the default outline on focus
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none', // Remove the default outline when focus is within the cell
          },
          // Ensure consistent scrollbar appearance across platforms
          '& ::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '& ::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '& ::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '& ::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
        // slots={{ toolbar: GridToolbar }}
      />
    </div>
  );
};

const CustomNoRowsOverlay = ({
  noDataMessage,
  message,
}: {
  noDataMessage?: string;
  message?: string;
}) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexDirection="column"
    height="100%"
    sx={{ border: '1px solid #999', borderRadius: '3px' }}
  >
    <Typography
      variant="h6"
      color="textSecondary"
      style={{
        fontSize: noDataMessage ? '20px' : '24px',
        fontWeight: '400',
        color: '#000000',
      }}
    >
      {noDataMessage ?? message}
    </Typography>
  </Box>
);
